import type { ReactNode } from "react";
import { useLocation } from "wouter";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import {
  IconBell,
  IconBook,
  IconBulb,
  IconCog,
  IconFolder,
  IconGift,
  IconGlobe,
  IconHome,
  IconImport,
  IconPlug,
  IconTemplate,
  IconPlus,
  IconSearch,
  IconShield,
} from "./Icons";
import { useNotifications } from "../notifications/NotificationsPanel";

type NavItem = {
  label: string;
  icon: ReactNode;
  href: string;
  /** When false, the link is omitted from the rail entirely. Keeps the
   * planned menu visible in source while preventing dead clicks. */
  enabled: boolean;
};

/**
 * All planned NavRail destinations. The exported BUILT_NAV_HREFS list
 * is the smoke suite's source of truth — if a link gets added here with
 * enabled:true but its route doesn't actually resolve, the smoke fails.
 */
const PRIMARY: ReadonlyArray<NavItem> = [
  { label: "Home", icon: <IconHome />, href: "/", enabled: true },
  { label: "Projects", icon: <IconFolder />, href: "/projects", enabled: true },
  { label: "Templates", icon: <IconTemplate />, href: "/templates", enabled: true },
  { label: "Published Projects", icon: <IconGlobe />, href: "/published", enabled: true },
  { label: "Integrations", icon: <IconPlug />, href: "/integrations", enabled: false },
  { label: "Security", icon: <IconShield />, href: "/security", enabled: false },
  { label: "Promotions", icon: <IconGift />, href: "/promotions", enabled: false },
  { label: "Notifications", icon: <IconBell />, href: "/notifications", enabled: true },
  { label: "Settings", icon: <IconCog />, href: "/settings", enabled: true },
];

const SECONDARY: ReadonlyArray<NavItem> = [
  { label: "Learn", icon: <IconBulb />, href: "/learn", enabled: false },
  { label: "Documentation", icon: <IconBook />, href: "/docs", enabled: false },
];

const TERTIARY: ReadonlyArray<NavItem> = [
  { label: "Refer & Earn", icon: <IconGift />, href: "/refer", enabled: false },
];

/** Hrefs that aren't real routes — they dispatch an event instead.
 * Excluded from the smoke's route-walker. */
const POPOVER_HREFS = new Set(["/settings", "/notifications"]);

/** Live route hrefs surfaced to the user. Exported so e2e/smoke can iterate. */
export const BUILT_NAV_HREFS: ReadonlyArray<string> = [
  ...PRIMARY,
  ...SECONDARY,
  ...TERTIARY,
]
  .filter((i) => i.enabled && !POPOVER_HREFS.has(i.href))
  .map((i) => i.href);

export function NavRail({ handle }: { handle: string }) {
  const [path, setLocation] = useLocation();
  const { unreadCount } = useNotifications();

  const isActive = (href: string) =>
    href === "/" ? path === "/" : path.startsWith(href);

  function badgeFor(item: NavItem): number | null {
    if (item.href === "/notifications" && unreadCount > 0) return unreadCount;
    return null;
  }

  return (
    <aside
      style={{
        width: 260,
        flexShrink: 0,
        height: "100vh",
        background: "var(--bg-panel)",
        borderRight: "1px solid var(--border-default)",
        display: "flex",
        flexDirection: "column",
        padding: "10px 10px 8px",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 6px" }}>
        <div
          aria-hidden
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            background:
              "conic-gradient(from 210deg, #f97316, #fb923c, #f59e0b, #fbbf24, #f97316)",
            display: "inline-block",
          }}
        />
        <div style={{ flex: 1 }} />
        <button
          type="button"
          aria-label="Search"
          style={{
            width: 26,
            height: 26,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "none",
            color: "var(--text-secondary)",
            cursor: "pointer",
            borderRadius: 6,
          }}
        >
          <IconSearch />
        </button>
      </div>

      <WorkspaceSwitcher handle={handle} />

      <button
        type="button"
        onClick={() => setLocation("/")}
        style={cardButtonStyle}
      >
        <IconPlus />
        <span>Create something new</span>
      </button>
      <button
        type="button"
        onClick={() => setLocation("/import")}
        style={cardButtonStyle}
      >
        <IconImport />
        <span>Import code or design</span>
      </button>
      <nav style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 4 }}>
        {PRIMARY.filter((i) => i.enabled).map((item) => (
          <NavLink
            key={item.label}
            item={item}
            active={isActive(item.href)}
            badge={badgeFor(item)}
            onClick={() => handleNavClick(item, setLocation)}
          />
        ))}
      </nav>

      <div style={{ flex: 1 }} />

      {(SECONDARY.some((i) => i.enabled) || TERTIARY.some((i) => i.enabled)) && (
        <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {SECONDARY.filter((i) => i.enabled).map((item) => (
            <NavLink
              key={item.label}
              item={item}
              active={false}
              onClick={() => handleNavClick(item, setLocation)}
            />
          ))}
          {TERTIARY.filter((i) => i.enabled).map((item) => (
            <NavLink
              key={item.label}
              item={item}
              active={false}
              onClick={() => handleNavClick(item, setLocation)}
            />
          ))}
        </nav>
      )}
    </aside>
  );
}

/**
 * Settings + Notifications are popovers — clicking the rail item
 * dispatches the global open event instead of navigating. Other rail
 * items still route normally. The `/settings` URL is still a valid
 * page (legacy, full-page).
 */
function handleNavClick(item: NavItem, setLocation: (path: string) => void) {
  if (item.href === "/settings") {
    window.dispatchEvent(new CustomEvent("vp:settings:open"));
    return;
  }
  if (item.href === "/notifications") {
    window.dispatchEvent(new CustomEvent("vp:notifications:open"));
    return;
  }
  setLocation(item.href);
}

const cardButtonStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  background: "var(--bg-panel-2)",
  border: "1px solid var(--border-default)",
  borderRadius: 8,
  padding: "9px 12px",
  color: "var(--text-primary)",
  fontSize: 13,
  cursor: "pointer",
  textAlign: "left",
};

function NavLink({
  item,
  active,
  onClick,
  badge,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
  badge?: number | null;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 10px",
        background: active ? "var(--bg-active)" : "transparent",
        border: "none",
        borderRadius: 6,
        color: active ? "var(--text-primary)" : "var(--text-secondary)",
        cursor: "pointer",
        textAlign: "left",
        fontSize: 13,
        width: "100%",
      }}
    >
      <span style={{ display: "inline-flex", color: active ? "var(--text-primary)" : "var(--text-secondary)" }}>
        {item.icon}
      </span>
      <span style={{ flex: 1 }}>{item.label}</span>
      {badge != null && badge > 0 && (
        <span
          data-testid={`navrail-badge-${item.label.toLowerCase()}`}
          aria-label={`${badge} unread`}
          style={{
            minWidth: 18,
            height: 18,
            padding: "0 5px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--accent-red)",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            borderRadius: 999,
          }}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
}
